import gsap from "gsap";

function horizontalLoop(items: HTMLElement[], config: {
    repeat?: number;
    speed?: number;
    reversed?: boolean;
    paddingRight?: number;
    snap?: number | false;
}) {
    items = gsap.utils.toArray(items);
    config = config || {};

    const tl = gsap.timeline({
        repeat: config.repeat,
        defaults: { ease: "none" },
        onReverseComplete: () => { tl.totalTime(tl.rawTime() + tl.duration() * 100); },
    });

    const length = items.length;
    const startX = items[0].offsetLeft;
    const widths: number[] = [];
    const xPercents: number[] = [];
    const pixelsPerSecond = (config.speed || 1) * 5;
    const snap = config.snap === false ? (v: number) => v : gsap.utils.snap(config.snap || 1);

    gsap.set(items, {
        xPercent: (i: number, el: HTMLElement) => {
            const w = (widths[i] = parseFloat(gsap.getProperty(el, "width", "px") as string));
            xPercents[i] = snap(
                (parseFloat(gsap.getProperty(el, "x", "px") as string) / w) * 100 +
                (gsap.getProperty(el, "xPercent") as number)
            );
            return xPercents[i];
        },
    });

    gsap.set(items, { x: 0 });

    const totalWidth =
        items[length - 1].offsetLeft +
        (xPercents[length - 1] / 100) * widths[length - 1] -
        startX +
        items[length - 1].offsetWidth * (gsap.getProperty(items[length - 1], "scaleX") as number) +
        (parseFloat(String(config.paddingRight)) || 0);

    for (let i = 0; i < length; i++) {
        const item = items[i];
        const curX = (xPercents[i] / 100) * widths[i];
        const distanceToStart = item.offsetLeft + curX - startX;
        const distanceToLoop = distanceToStart + widths[i] * (gsap.getProperty(item, "scaleX") as number);

        tl.to(
            item,
            {
                xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
                duration: distanceToLoop / pixelsPerSecond,
            },
            0
        ).fromTo(
            item,
            {
                xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100),
            },
            {
                xPercent: xPercents[i],
                duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
                immediateRender: false,
            },
            distanceToLoop / pixelsPerSecond
        );
    }

    tl.progress(1, true).progress(0, true);

    if (config.reversed) {
        tl.vars.onReverseComplete!();
        tl.reverse();
    }

    return tl;
}

function initMarquee(container: HTMLElement, trackSelector = ".njamito-marquee__track") {
    const track = container.querySelector<HTMLElement>(trackSelector);
    if (!track) return null;

    const speed = parseFloat(container.dataset.marqueeSpeed || "1");
    const direction = container.dataset.marqueeDirection || "left";
    const reversed = direction === "right";

    const items = Array.from(track.children) as HTMLElement[];
    if (items.length === 0) return null;

    // Convert CSS gap to margin-right since horizontalLoop positions items individually
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    if (gap) {
        track.style.gap = "0px";
        items.forEach(item => {
            item.style.marginRight = `${gap}px`;
        });
    }

    const paddingRight = gap || parseFloat(gsap.getProperty(items[0], "marginRight", "px") as string) || 0;

    const tl = horizontalLoop(items, {
        repeat: -1,
        speed,
        reversed,
        paddingRight,
    });

    container.addEventListener("mouseenter", () => {
        gsap.to(tl, { timeScale: 0, duration: 0.5, overwrite: true });
    });

    container.addEventListener("mouseleave", () => {
        gsap.to(tl, { timeScale: reversed ? -1 : 1, duration: 0.5, overwrite: true });
    });

    return tl;
}

window.addEventListener("load", () => {
    document.querySelectorAll<HTMLElement>(".njamito-marquee").forEach((el) => {
        try {
            initMarquee(el);
        } catch (e) {
            console.warn("Marquee init failed:", e);
        }
    });
});
